import React from "react";
import { FaFacebookF, FaInstagram, FaTiktok, FaWhatsapp } from "react-icons/fa";
import { Link } from "react-router-dom";
import { useLanguage } from "./idiomas";
import { motion, AnimatePresence } from "framer-motion";

export default function Footer() {
  const { language, toggleLanguage } = useLanguage();

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  // ======================================================
  // 🌍 TEXTOS POR IDIOMA
  // ======================================================
  const texts = {
    es: {
      about: "Sobre Nosotros",
      services: "Servicios",
      contact: "Contáctanos",
      rights: "Todos los derechos reservados.",
      serviceList: ["Legal", "Contable", "Desarrollo", "Notarial", "Y más..."],
      description:
        "Somos un bufete comprometido con ofrecer soluciones legales personalizadas y efectivas. Confiabilidad, experiencia y dedicación a cada cliente.",
      schedule: "🕒 Lunes a Viernes: 8:00 AM - 4:00 PM",
      address: "📍 Barrio Bella Vista, Golfito, Puntarenas, Costa Rica",
      phone: "📞 +506 8553-1009",
      email: "✉️ Legalinnova.cr@gmail.com",
    },
    en: {
      about: "About Us",
      services: "Services",
      contact: "Contact Us",
      rights: "All rights reserved.",
      serviceList: ["Legal", "Accounting", "Development", "Notarial", "And more..."],
      description:
        "We are a law firm committed to providing personalized and effective legal solutions. Reliability, experience and dedication to every client.",
      schedule: "🕒 Monday to Friday: 8:00 AM - 4:00 PM",
      address: "📍 Barrio Bella Vista, Golfito, Puntarenas, Costa Rica",
      phone: "📞 +506 8553-1009",
      email: "✉️ Legalinnova.cr@gmail.com",
    },
    fr: {
      about: "À Propos de Nous",
      services: "Services",
      contact: "Contactez-nous",
      rights: "Tous droits réservés.",
      serviceList: ["Juridique", "Comptabilité", "Développement", "Notariat", "Et plus..."],
      description:
        "Nous sommes un cabinet d’avocats engagé à offrir des solutions juridiques personnalisées et efficaces. Fiabilité, expérience et dévouement envers chaque client.",
      schedule: "🕒 Lundi à Vendredi : 8h00 - 16h00",
      address: "📍 Barrio Bella Vista, Golfito, Puntarenas, Costa Rica",
      phone: "📞 +506 8553-1009",
      email: "✉️ Legalinnova.cr@gmail.com",
    },
  };

  const t = texts[language];

  // ======================================================
  // 🏳️ SELECTOR DE IDIOMA (BANDERAS)
  // ======================================================
  const flags = [
    { code: "es", src: "https://flagcdn.com/w40/es.png", alt: "Español" },
    { code: "en", src: "https://flagcdn.com/w40/gb.png", alt: "English" },
    { code: "fr", src: "https://flagcdn.com/w40/fr.png", alt: "Français" },
  ];

  // ======================================================
  // 🌐 FUNCIÓN PARA CAMBIAR IDIOMA + SCROLL + TRANSICIÓN
  // ======================================================
  const handleLanguageChange = (code) => {
    if (language !== code) {
      toggleLanguage(code);
      scrollToTop();
    }
  };

  return (
    <footer className="bg-[#0B1C2C] text-gray-300 py-10 transition-all duration-500">
      <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row gap-8 items-start justify-between border-t border-gray-700 pt-10">
        <AnimatePresence mode="wait">
          <motion.div
            key={language} // 👈 Hace la animación al cambiar idioma
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.4 }}
            className="w-full flex flex-col md:flex-row gap-8 items-start justify-between"
          >
            {/* ABOUT US */}
            <div className="flex-1 space-y-4 text-left">
              <h3 className="text-white font-semibold text-xl">{t.about}</h3>
              <p className="text-sm leading-relaxed">{t.description}</p>

              {/* Social Icons */}
              <div className="flex space-x-4 mt-4">
                <a
                  href="https://www.facebook.com/LegalInnovaCR/?locale=es_LA"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1877F2] text-white hover:opacity-80 transition"
                >
                  <FaFacebookF />
                </a>
                <a
                  href="https://www.instagram.com/legalinnovacosta/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-[#E1306C] text-white hover:opacity-80 transition"
                >
                  <FaInstagram />
                </a>
                <a
                  href="https://www.tiktok.com/@legalinnova"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-white text-black hover:opacity-80 transition"
                >
                  <FaTiktok />
                </a>
                <a
                  href="https://wa.me/50685531009"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-[#25D366] text-white hover:opacity-80 transition"
                >
                  <FaWhatsapp />
                </a>
              </div>
            </div>

            {/* SERVICES */}
            <div className="flex-1 space-y-4 text-left md:text-center">
              <h3 className="text-white font-semibold text-xl">{t.services}</h3>
              <ul className="space-y-2 text-sm">
                {t.serviceList.map((servicio, idx) => (
                  <li key={idx}>
                    <Link
                      to="/services"
                      onClick={scrollToTop}
                      className="hover:text-white transition cursor-pointer"
                    >
                      {servicio}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* CONTACT */}
            <div className="flex-1 space-y-4 text-left">
              <h3 className="text-white font-semibold text-xl">{t.contact}</h3>
              <ul className="space-y-2 text-sm">
                <li>{t.phone}</li>
                <li>{t.address}</li>
                <li>{t.email}</li>
                <li>{t.schedule}</li>
              </ul>

              {/* Language Selector */}
              <div className="mt-6 flex items-center space-x-3">
                {flags.map((flag) => (
                  <button
                    key={flag.code}
                    onClick={() => handleLanguageChange(flag.code)}
                    className={`w-8 h-8 rounded-full overflow-hidden border-2 hover:scale-110 transition-transform ${
                      language === flag.code ? "border-white" : "border-transparent"
                    }`}
                  >
                    <img
                      src={flag.src}
                      alt={flag.alt}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="border-t border-gray-700 mt-10 pt-6 text-center text-sm text-gray-400 transition-all duration-300">
        © {new Date().getFullYear()} Legal Innova. {t.rights}
      </div>
    </footer>
  );
}
